import { useEffect, useId, useRef, useState } from 'react';
import {
  CheckIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { ShareExpiry } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import { TOUCH_TARGET } from '../components/touch-target';
import { interpolate } from '../gallery/format';
import { browserShareDeps, shareLink } from '../lib/share';
import { type ShareTarget, ShareApiError, createShare, shareUrl } from './api';

/**
 * Fenêtre de CRÉATION d'un lien public (XPHOTO-31, ADR-0009).
 *
 * ── DEUX TEMPS, ET LE SECOND EST IRRÉVERSIBLE ─────────────────────────────────────
 *
 * 1. le formulaire (titre, échéance, copies allégées) ;
 * 2. le LIEN, affiché une seule fois. Le jeton brut n'existe qu'à cet instant : la base
 *    n'en stocke que le SHA-256, et rien ne permet de le ré-afficher. La fenêtre le dit
 *    en toutes lettres plutôt que de laisser l'utilisateur le découvrir.
 *
 * ── L'AVERTISSEMENT SUIT LA CASE ──────────────────────────────────────────────────
 *
 * Sans « copies allégées », le visiteur reçoit les fichiers d'origine AVEC leurs
 * métadonnées — dont le lieu de prise de vue. C'est le défaut, c'est un choix assumé
 * (ADR-0009), et c'est précisément pour ça qu'il doit être écrit à l'écran. Cocher la
 * case réduit l'avertissement à ce qui reste vrai : les vidéos sortent toujours en
 * fichier d'origine.
 */

/**
 * Options d'échéance, avec le suffixe de clé i18n qui leur correspond. Les valeurs du
 * contrat (`7d`, `30d`, `1y`) ne peuvent pas servir telles quelles de clés de
 * catalogue : un identifiant TypeScript ne commence pas par un chiffre, et les citer
 * ferait diverger la forme des clés du reste des catalogues.
 */
const EXPIRY_OPTIONS: readonly { value: ShareExpiry; key: 'd7' | 'd30' | 'y1' | 'never' }[] = [
  { value: '7d', key: 'd7' },
  { value: '30d', key: 'd30' },
  { value: '1y', key: 'y1' },
  { value: 'never', key: 'never' },
];

interface CreateShareModalProps {
  target: ShareTarget;
  /** Libellé du périmètre, déjà localisé (« Dossier Vacances », « 12 médias »…). */
  scopeLabel: string;
  /** Titre proposé par défaut, dérivé du contexte. */
  defaultTitle: string;
  onClose: () => void;
}

export function CreateShareModal({
  target,
  scopeLabel,
  defaultTitle,
  onClose,
}: CreateShareModalProps): React.JSX.Element {
  const { t } = useI18n();
  const [title, setTitle] = useState(defaultTitle);
  const [expiresIn, setExpiresIn] = useState<ShareExpiry>('30d');
  const [lightweight, setLightweight] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Le lien créé — présent = second temps de la fenêtre. */
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalA11y({
    dialogRef,
    initialFocusRef: titleRef,
    // Échap ne ferme PAS pendant l'appel réseau : le lien est peut-être déjà créé
    // côté serveur, et le fermer maintenant le perdrait sans possibilité de retour.
    ...(busy ? {} : { onEscape: onClose }),
  });

  useEffect(() => {
    if (link === null) titleRef.current?.select();
    else {
      // Le lien est sélectionné d'emblée : un Ctrl+C suffit, même si le presse-papiers
      // est refusé (contexte non sécurisé).
      linkRef.current?.focus();
      linkRef.current?.select();
    }
  }, [link]);

  const onSubmit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const created = await createShare(target, {
        title: title.trim(),
        expiresIn,
        shareOriginals: !lightweight,
      });
      setLink(shareUrl(created.secret, window.location.origin));
    } catch (err) {
      setError(
        err instanceof ShareApiError && err.status === 403
          ? t('share.create.forbidden')
          : t('share.create.failed'),
      );
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (link === null) return;
    const outcome = await shareLink(link, title, browserShareDeps(navigator));
    if (outcome.kind === 'copied') setCopied(true);
  };

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-lg border border-border bg-surface p-5 text-fg shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold">
            {link === null ? t('share.create.title') : t('share.create.doneTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={t('share.close')}
            className={`-me-1 -mt-1 inline-flex ${TOUCH_TARGET} items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50`}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {link === null ? (
          <>
            <p className="mt-1 text-sm text-muted">{t('share.create.intro')}</p>

            <label className="mt-4 block">
              <span className="mb-1.5 block text-xs font-semibold">
                {t('share.create.titleLabel')}
              </span>
              <input
                ref={titleRef}
                type="text"
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy}
                className="w-full rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              />
            </label>

            <div className="mt-4">
              <span className="mb-1.5 block text-xs font-semibold">
                {t('share.create.scopeLabel')}
              </span>
              <p className="flex items-center gap-2.5 rounded-md border border-border bg-surface-2 px-3 py-2.5 text-sm">
                {target.kind === 'folder' ? (
                  <FolderIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                ) : (
                  <PhotoIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                )}
                <span className="min-w-0 break-words">{scopeLabel}</span>
              </p>
            </div>

            <fieldset className="mt-4">
              <legend className="mb-1.5 text-xs font-semibold">
                {t('share.create.expiryLabel')}
              </legend>
              <div className="flex flex-wrap gap-2">
                {EXPIRY_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                      expiresIn === option.value
                        ? 'border-brand bg-brand/10 font-semibold text-brand'
                        : 'border-border-strong text-muted hover:bg-surface-2'
                    }`}
                  >
                    <input
                      type="radio"
                      name="share-expiry"
                      value={option.value}
                      checked={expiresIn === option.value}
                      onChange={() => setExpiresIn(option.value)}
                      disabled={busy}
                      className="sr-only"
                    />
                    {t(`share.create.expiry.${option.key}`)}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="mt-4 flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={lightweight}
                onChange={(e) => setLightweight(e.target.checked)}
                disabled={busy}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong accent-brand"
              />
              <span>
                <span className="block text-sm font-semibold">{t('share.create.lightweight')}</span>
                <span className="mt-0.5 block text-xs text-muted">
                  {t('share.create.lightweightHint')}
                </span>
              </span>
            </label>

            <p className="mt-4 flex gap-2.5 rounded-md border border-warning-text/40 bg-warning-text/10 px-3 py-2.5 text-xs leading-relaxed">
              <ExclamationTriangleIcon
                className="mt-px h-4 w-4 shrink-0 text-warning-text"
                aria-hidden="true"
              />
              <span>
                {lightweight ? t('share.create.warnVideosOnly') : t('share.create.warnOriginals')}
              </span>
            </p>

            {error !== null && (
              <p role="alert" className="mt-3 text-sm text-danger">
                {error}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className={`inline-flex ${TOUCH_TARGET} items-center justify-center rounded-md px-4 text-sm font-medium text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50`}
              >
                {t('share.create.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void onSubmit()}
                disabled={busy || title.trim().length === 0}
                className={`inline-flex ${TOUCH_TARGET} items-center justify-center rounded-md bg-brand px-4 text-sm font-semibold text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50`}
              >
                {busy ? t('share.create.submitting') : t('share.create.submit')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-muted">{t('share.create.doneIntro')}</p>
            <input
              ref={linkRef}
              type="text"
              readOnly
              value={link}
              aria-label={t('share.linkLabel')}
              className="mt-4 w-full rounded-md border border-border-strong bg-surface-2 px-3 py-2 font-mono text-xs text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />
            <p className="mt-2 flex gap-2.5 rounded-md border border-warning-text/40 bg-warning-text/10 px-3 py-2.5 text-xs leading-relaxed">
              <ExclamationTriangleIcon
                className="mt-px h-4 w-4 shrink-0 text-warning-text"
                aria-hidden="true"
              />
              <span>{t('share.create.onceOnly')}</span>
            </p>

            <div className="mt-5 flex items-center justify-end gap-2 border-t border-border pt-4">
              <span role="status" aria-live="polite" className="me-auto text-sm text-success">
                {copied && (
                  <span className="flex items-center gap-1.5">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    {t('share.copied')}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => void onCopy()}
                className={`inline-flex ${TOUCH_TARGET} items-center justify-center rounded-md border border-border-strong px-4 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
              >
                {t('share.create.copy')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className={`inline-flex ${TOUCH_TARGET} items-center justify-center rounded-md bg-brand px-4 text-sm font-semibold text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
              >
                {t('share.create.done')}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

/** Libellé du périmètre, dérivé de la cible — une seule règle, deux appelants. */
export function scopeLabelFor(
  target: ShareTarget,
  t: (
    key: 'share.create.scopeRoot' | 'share.create.scopeFolder' | 'share.create.scopeSelection',
  ) => string,
): string {
  if (target.kind === 'folder') {
    return target.folderPath === ''
      ? t('share.create.scopeRoot')
      : interpolate(t('share.create.scopeFolder'), { folder: target.folderPath });
  }
  return interpolate(t('share.create.scopeSelection'), { count: target.assetIds.length });
}
